import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Badge, Button, Card, CardHeader, ErrorSummary, Input, Modal, ModalFooter } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import { quotePricingService, type BrokerPricingRequest } from '@/services/insurerQuoteDesk';
import { formatCurrency } from '@/utils';

/**
 * #399: the broker / POSP side of the insurer Quote Desk, on the quote screen.
 *
 * "Request insurer pricing" sends this quote request to one or more insurers'
 * underwriters (one pricing request per insurer). Each row shows where it
 * stands; an information request can be answered inline; an open one can be
 * withdrawn. An approval lands as an ordinary option in the premium options
 * card above, badged "Insurer approved".
 */

// eslint-disable-next-line react-refresh/only-export-components
export function pricingStatusText(pr: BrokerPricingRequest): string {
  switch (pr.status) {
    case 'open':
    case 'in_review':
      return `With ${pr.insurer.name} underwriting`;
    case 'info_requested':
      return 'Waiting for your reply';
    case 'approved':
      return `Approved: ${pr.quote ? formatCurrency(Number(pr.quote.total_premium)) : ''}`.trim();
    case 'declined':
      return `Declined: ${pr.decision_label || pr.decision_reason}`;
    case 'withdrawn':
      return 'Withdrawn';
    default:
      return pr.status_display;
  }
}

const VARIANT: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  approved: 'success',
  declined: 'error',
  info_requested: 'warning',
  open: 'info',
  in_review: 'info',
};

function FlagModal({ requestId, onClose }: { requestId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const insurers = useQuery({
    queryKey: ['quote-pricing-insurers', requestId],
    queryFn: () => quotePricingService.insurers(requestId),
  });
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [target, setTarget] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const submit = useMutation({
    mutationFn: async () => {
      const failures: string[] = [];
      for (const insurer of Array.from(chosen)) {
        try {
          await quotePricingService.flag(requestId, { insurer, note, target_premium: target || null });
        } catch (err) {
          const name = insurers.data?.find((i) => i.id === insurer)?.name ?? 'Insurer';
          failures.push(`${name}: ${getErrorMessage(err)}`);
        }
      }
      return failures;
    },
    onSuccess: (failures) => {
      queryClient.invalidateQueries({ queryKey: ['quote-pricing-requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['quote-pricing-insurers', requestId] });
      if (failures.length) {
        setErrors(failures);
        return;
      }
      toast.success('Sent for insurer pricing');
      onClose();
    },
  });

  return (
    <Modal isOpen onClose={onClose} title="Request insurer pricing" size="lg">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErrors([]);
          if (!chosen.size) return setErrors(['Choose at least one insurer.']);
          if (!note.trim()) return setErrors(['Tell the underwriter what you need priced.']);
          submit.mutate();
        }}
      >
        <ErrorSummary messages={errors} />
        <fieldset className="mb-4">
          <legend className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Insurers</legend>
          {insurers.isLoading && <p className="text-sm text-gray-500">Loading insurers...</p>}
          {insurers.data?.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Your brokerage is not connected to any insurer.</p>
          )}
          <ul className="space-y-2">
            {insurers.data?.map((insurer) => (
              <li key={insurer.id}>
                <label
                  className={`flex items-start gap-2 text-sm ${
                    insurer.eligible ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={!insurer.eligible}
                    checked={chosen.has(insurer.id)}
                    onChange={(e) =>
                      setChosen((current) => {
                        const next = new Set(current);
                        if (e.target.checked) next.add(insurer.id);
                        else next.delete(insurer.id);
                        return next;
                      })
                    }
                  />
                  <span>
                    {insurer.name}
                    {insurer.eligible ? (
                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">(responds within {insurer.tat_hours}h)</span>
                    ) : (
                      <span className="block text-xs">{insurer.reason}</span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
        <label htmlFor="pricing-note" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Note for the underwriter
        </label>
        <textarea
          id="pricing-note"
          rows={3}
          maxLength={2000}
          className="mb-3 mt-1 w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Input
          id="pricing-target"
          label="Target premium (optional)"
          type="number"
          min={0}
          step="0.01"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
        />
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={submit.isPending}>
            Send
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function PricingRow({ pr, requestId, canAct }: { pr: BrokerPricingRequest; requestId: string; canAct: boolean }) {
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['quote-pricing-requests', requestId] });
  const send = useMutation({
    mutationFn: () => quotePricingService.reply(pr.id, reply),
    onSuccess: () => {
      setReply('');
      toast.success('Reply sent');
      refresh();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const withdraw = useMutation({
    mutationFn: () => quotePricingService.withdraw(pr.id),
    onSuccess: () => {
      toast.success('Pricing request withdrawn');
      refresh();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const question = [...pr.messages].reverse().find((m) => m.kind === 'info_request');

  return (
    <li className="py-3" data-testid="pricing-request-row">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-medium text-gray-900 dark:text-white">{pr.insurer.name}</span>{' '}
          <Badge size="sm" variant={VARIANT[pr.status] ?? 'default'}>
            {pricingStatusText(pr)}
          </Badge>
          {pr.is_overdue && (
            <span className="ml-2">
              <Badge size="sm" variant="warning">
                Insurer response late
              </Badge>
            </span>
          )}
          {pr.insurer_unavailable && pr.can_withdraw && (
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Insurer unavailable</span>
          )}
        </div>
        {canAct && pr.can_withdraw && (
          <Button size="sm" variant="ghost" isLoading={withdraw.isPending} onClick={() => withdraw.mutate()}>
            Withdraw
          </Button>
        )}
      </div>
      {pr.stale && (
        <p
          role="status"
          className="mt-2 rounded bg-amber-50 p-2 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
        >
          Request changed since insurer priced it - ask for re-pricing. This option cannot be selected until{' '}
          {pr.insurer.name} re-prices it.
        </p>
      )}
      {pr.status === 'declined' && pr.decision_reason && (
        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{pr.decision_reason}</p>
      )}
      {pr.can_reply && question && (
        <div className="mt-2 rounded bg-amber-50 p-2 text-sm dark:bg-amber-900/20">
          <p className="text-gray-800 dark:text-gray-200">{question.body}</p>
          {canAct && (
            <form
              className="mt-2 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (reply.trim()) send.mutate();
              }}
            >
              <label htmlFor={`reply-${pr.id}`} className="sr-only">
                Reply to {pr.insurer.name}
              </label>
              <input
                id={`reply-${pr.id}`}
                className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                value={reply}
                maxLength={2000}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Your reply"
              />
              <Button size="sm" type="submit" isLoading={send.isPending} disabled={!reply.trim()}>
                Reply
              </Button>
            </form>
          )}
        </div>
      )}
    </li>
  );
}

export function QuotePricingRequestsCard({ requestId, canAct = true }: { requestId: string; canAct?: boolean }) {
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: ['quote-pricing-requests', requestId],
    queryFn: () => quotePricingService.list(requestId),
    refetchOnWindowFocus: true,
  });
  const rows = query.data ?? [];

  return (
    <Card>
      <CardHeader
        title="Insurer pricing"
        subtitle="Ask an insurer's underwriters to price this quote"
        action={
          canAct ? (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
              Request insurer pricing
            </Button>
          ) : undefined
        }
      />
      {query.isError ? (
        <p className="text-sm text-red-600 dark:text-red-400">{getErrorMessage(query.error)}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No insurer has been asked to price this quote.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((pr) => (
            <PricingRow key={pr.id} pr={pr} requestId={requestId} canAct={canAct} />
          ))}
        </ul>
      )}
      {open && <FlagModal requestId={requestId} onClose={() => setOpen(false)} />}
    </Card>
  );
}

export default QuotePricingRequestsCard;
